/**
 * AttackPathsCard — top 5 attack paths sorted by risk severity. Each
 * row: path id, asset name, LevelBadge for risk, and the treatment
 * decision as plain text ("None" when missing).
 *
 * Pure presentation; consumes `data.topAttackPaths`.
 */
import React from 'react';
import LevelBadge from '../../../../components/ui/LevelBadge';
import {
  TypographyH2,
  TypographyMuted,
  TypographySmall,
} from '../../../../components/ui/Typography';
import {
  Empty,
  EmptyHeader,
  EmptyTitle,
  EmptyDescription,
} from '../../../../components/ui/Empty';
import { getSeverityColor } from '../../../../utils/severityStyle';
import type { AggregatedOverviewData } from '../../aggregate/types';
import styles from './AttackPathsCard.module.css';

interface AttackPathsCardProps {
  data: AggregatedOverviewData;
}

const cap = (s: string) => (s.length === 0 ? s : s[0].toUpperCase() + s.slice(1));

export default function AttackPathsCard({ data }: AttackPathsCardProps) {
  const rows = data.topAttackPaths;

  if (rows.length === 0) {
    return (
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <TypographyH2 className={styles.cardTitle}>Top Risk Attack Paths</TypographyH2>
        </div>
        <Empty>
          <EmptyHeader>
            <EmptyTitle>No attack paths yet</EmptyTitle>
            <EmptyDescription>Populates after Step 5.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <TypographyH2 className={styles.cardTitle}>Top Risk Attack Paths</TypographyH2>
        <TypographyMuted className={styles.cardMeta}>Top {rows.length}</TypographyMuted>
      </div>
      <div className={styles.table}>
        <div className={styles.tableHead}>
          <TypographyMuted className={styles.th}>Path</TypographyMuted>
          <TypographyMuted className={styles.th}>Asset</TypographyMuted>
          <TypographyMuted className={styles.th}>Risk</TypographyMuted>
          <TypographyMuted className={styles.th}>Treatment</TypographyMuted>
        </div>
        {rows.map((r) => {
          const riskLabel = cap(r.risk);
          return (
            <div key={r.id} className={styles.tableRow}>
              <TypographySmall className={styles.id}>{r.id || '—'}</TypographySmall>
              <TypographySmall className={styles.asset} title={r.asset}>
                {r.asset}
              </TypographySmall>
              <span>
                <LevelBadge
                  color={getSeverityColor(riskLabel, { scheme: 'athena' })}
                  textColor=""
                  label={riskLabel}
                  size="sm"
                />
              </span>
              <TypographySmall>
                {r.treatment ? cap(r.treatment) : 'None'}
              </TypographySmall>
            </div>
          );
        })}
      </div>
    </div>
  );
}
